// Downloads Section - Modern downloads page (replacement for legacy downloads)
// Follows the Section, Component & Manager System architecture

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '../../components/Layout';
import { DownloadsManager } from './managers/downloads-manager';
import { useManager } from '../../core';
import { useAuth } from '../../contexts/AuthContext';
import { DownloadsLayout, DownloadsGrid } from './components';
import './DownloadsSection.css';

export function DownloadsSection() {
  const { manager, isInitialized } = useManager(DownloadsManager);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [downloads, setDownloads] = useState([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);

  // Extract URL parameters
  const urlQuery = searchParams.get('query') || '';
  const urlSort = searchParams.get('sort') || '';
  const urlTags = searchParams.get('tags')?.split(',').filter(Boolean) || [];

  const fetchDownloads = useCallback(async () => {
    if (!manager) return;
    setDownloadsLoading(true);
    try {
      const list = await manager.fetchPublicDownloads({
        query: urlQuery,
        sort: urlSort,
        tags: urlTags,
      });
      setDownloads(list);
    } catch (err) {
      console.error('Failed to fetch downloads:', err);
      setDownloads([]);
    } finally {
      setDownloadsLoading(false);
    }
  }, [manager, urlQuery, urlSort, urlTags.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isInitialized && manager) {
      fetchDownloads();
    }
  }, [isInitialized, manager, fetchDownloads]);

  const handleDownloadClick = useCallback((d) => {
    if (d?.id) navigate(`/downloads/details?downloadId=${d.id}`);
  }, [navigate]);

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--downloads">
        <div className="loading-container">
          <div>Loading downloads...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout contentClassName="main-content--downloads">
      <div className="downloads-section">
        <DownloadsLayout
          admin={false}
          user={user}
          navigate={navigate}
          urlQuery={urlQuery}
          urlSort={urlSort}
          urlTags={urlTags}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        >
          <DownloadsGrid
            downloads={downloads}
            loading={downloadsLoading}
            admin={false}
            emptyMessage="No downloads available yet. Check back soon!"
            sortMode={urlSort}
            onDownloadClick={handleDownloadClick}
          />
        </DownloadsLayout>
      </div>
    </MainLayout>
  );
}